<template>
  <section class="career-guide-section">
    <div class="careerGuideTop">
      <span class="careerGuideTitle">{{ t('careerGuide.section11Title') }}</span>
      <div class="careerGuideTopRight">
        <div class="d-flex align-items-center">
          <span class="careerGuideTopRight_title">{{ t('careerGuide.cycleLabel') }}</span>
          <select v-model="selectedCycle" class="selectCycle">
            <option v-for="item in cycles" :key="item.value || 'all'" :value="item.value">
              {{ item.text }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="cycleGraph">
      <CareerLineChart :chart-data="lineChartData" />
    </div>

    <div class="graphInfo">
      <div v-for="item in graphInfo" :key="item.text" class="graphInfo__item">
        <div class="graphInfo__color" :style="{ backgroundColor: item.color }"></div>
        <span class="graphInfo__title">{{ item.text }}</span>
      </div>
    </div>

    <div class="sectionTableBg">
      <table class="sectionTable">
        <thead>
          <tr>
            <th rowspan="2">{{ t('careerGuide.classLabel') }}</th>
            <th rowspan="2">{{ t('careerGuide.cycleLabel') }}</th>
            <th colspan="4" class="text-center">{{ t('careerGuide.avgGradeByCycle') }}</th>
          </tr>
          <tr>
            <th>{{ t('careerGuide.q1') }}</th>
            <th>{{ t('careerGuide.q2') }}</th>
            <th>{{ t('careerGuide.q3') }}</th>
            <th>{{ t('careerGuide.q4') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in sectionTable" :key="`section-row-${index}`">
            <td>{{ item.parallel }}</td>
            <td>{{ item.cycle }}</td>
            <td>{{ item.first_quarter || '-' }}</td>
            <td>{{ item.second_quarter || '-' }}</td>
            <td>{{ item.third_quarter || '-' }}</td>
            <td>{{ item.last_quarter || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="sectionTableBgSmall">
      <div class="sectionTableBgSmall__body" v-for="item in mobileTable" :key="`mobile-${item.text}`">
        <span class="sectionTableBgSmall__body__title">{{ item.text }} {{ t('class') }}</span>
        <div class="sectionTableBgSmall__body__list" v-for="itemSection in item.quarters" :key="itemSection.cycle">
          <span class="sectionTableBgSmall__body__cycle">{{ itemSection.cycle }}</span>
          <span class="sectionTableBgSmall__body__quarter"
            ><span class="sectionTableBgSmall__body__quarter_title">{{ t('careerGuide.q1') }}:</span>
            {{ itemSection.first_quarter || t('careerGuide.notSet') }}</span
          >
          <span class="sectionTableBgSmall__body__quarter"
            ><span class="sectionTableBgSmall__body__quarter_title">{{ t('careerGuide.q2') }}:</span>
            {{ itemSection.second_quarter || t('careerGuide.notSet') }}</span
          >
          <span class="sectionTableBgSmall__body__quarter"
            ><span class="sectionTableBgSmall__body__quarter_title">{{ t('careerGuide.q3') }}:</span>
            {{ itemSection.third_quarter || t('careerGuide.notSet') }}</span
          >
          <span class="sectionTableBgSmall__body__quarter"
            ><span class="sectionTableBgSmall__body__quarter_title">{{ t('careerGuide.q4') }}:</span>
            {{ itemSection.last_quarter || t('careerGuide.notSet') }}</span
          >
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import CareerLineChart from '@/components/career-guide/CareerLineChart.vue';

const props = defineProps({
  sectionTable: {
    type: Array,
    default: () => []
  },
  graphInfo: {
    type: Array,
    default: () => []
  }
});

const { t } = useI18n();

const selectedCycle = ref(null);

const cycles = computed(() => {
  const values = [...new Set((props.sectionTable || []).map((item) => item?.cycle).filter(Boolean))];
  return [{ text: t('all'), value: null }, ...values.map((value) => ({ text: value, value }))];
});

const lineChartData = computed(() => {
  const data = props.sectionTable || [];
  const cyclesList = selectedCycle.value ? [selectedCycle.value] : [...new Set(data.map((item) => item.cycle))];
  const parallels = [...new Set(data.map((item) => item.parallel))].sort((a, b) => Number(a) - Number(b));
  const labels = parallels.flatMap((parallel) => [1, 2, 3, 4].map((quarter) => `${parallel}:${quarter}`));

  const datasets = cyclesList.map((cycle) => {
    const matched = props.graphInfo.find((item) => String(item.text || '').startsWith(String(cycle || '').trim()));
    const color = matched?.color || '#2D9CDB';
    const points = parallels.flatMap((parallel) => {
      const row = data.find((item) => item.parallel === parallel && item.cycle === cycle);
      if (!row) return [null, null, null, null];
      return [row.first_quarter, row.second_quarter, row.third_quarter, row.last_quarter];
    });

    return {
      label: cycle,
      data: points,
      borderColor: color,
      backgroundColor: matched?.backgroundColor
    };
  });

  return { labels, datasets };
});

const mobileTable = computed(() => {
  const result = [];
  (props.sectionTable || []).forEach((item) => {
    let target = result.find((entry) => entry.text === item.parallel);
    if (!target) {
      target = {
        text: item.parallel,
        quarters: []
      };
      result.push(target);
    }

    target.quarters.push({
      cycle: item.cycle,
      first_quarter: item.first_quarter,
      second_quarter: item.second_quarter,
      third_quarter: item.third_quarter,
      last_quarter: item.last_quarter
    });
  });
  return result;
});
</script>

<style scoped>
.careerGuideTop {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.careerGuideTopRight_title {
  color: #474a57;
  font-size: 14px;
  margin-right: 8px;
}

.selectCycle {
  background: #fff;
  border: 1px solid #ebecee;
  width: 220px;
  height: 36px;
  border-radius: 4px;
}

.careerGuideTitle {
  font-family: Roboto, sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28px;
  color: #18191f;
}

.cycleGraph {
  margin-top: 16px;
}

.graphInfo {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.graphInfo__item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.graphInfo__color {
  width: 9px;
  height: 9px;
  min-width: 9px;
  border-radius: 9px;
}

.graphInfo__title {
  font-size: 14px;
}

.sectionTableBg {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 16px;
}

.sectionTable {
  width: 100%;
  border-collapse: collapse;
}

.sectionTable th,
.sectionTable td {
  border: 1px solid #dee2e6;
  padding: 10px;
  font-size: 14px;
}

.sectionTable thead {
  background: #f5f6f7;
}

.sectionTableBgSmall {
  display: none;
}

@media screen and (max-width: 768px) {
  .careerGuideTop {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .careerGuideTitle {
    font-size: 16px;
    line-height: 24px;
  }

  .selectCycle {
    width: 100%;
  }
}

@media screen and (max-width: 575px) {
  .sectionTableBg {
    display: none;
  }

  .sectionTableBgSmall {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
  }

  .sectionTableBgSmall__body {
    border: 1px solid #e5e7e8;
    box-shadow: 0 1px 2px rgba(27, 36, 44, 0.12);
    background: #fff;
    border-radius: 12px;
    padding: 16px;
  }

  .sectionTableBgSmall__body__title {
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;
  }

  .sectionTableBgSmall__body__list {
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid #e5e7e8;
    padding-bottom: 8px;
    margin-bottom: 8px;
  }

  .sectionTableBgSmall__body__cycle {
    font-weight: 500;
    font-size: 16px;
    margin: 8px 0;
  }

  .sectionTableBgSmall__body__quarter {
    font-size: 14px;
    line-height: 20px;
  }

  .sectionTableBgSmall__body__quarter_title {
    color: #737982;
  }
}
</style>
